import { __ } from "@wordpress/i18n";
import {
    createContext,
    useState,
    useEffect,
    useCallback,
} from "@wordpress/element";
import apiFetch from "@wordpress/api-fetch";
import { useToast } from "../hook/useToast";

apiFetch.use(apiFetch.createNonceMiddleware(WPBEAN_QC_API.nonce));

export const RulesContext = createContext({
    shortcodes: null,
    filteredShortcodes: null,
    searchTerm: "",
    setSearchTerm: null,
    filterStatus: null,
    setFilterStatus: null,
    selectedItems: null,
    setSelectedItems: null,
    showAddModal: null,
    setShowAddModal: null,
    editShortcodeData: null,
    setEditShortcodeData: null,
    getShortcode: (shortcodeId) => {},
    deleteShortcode: async (id) => {},
    handleSelectItem: (id, checked) => {},
    handleSelectAll: (checked) => {},
    loading: {},
    error: null,
    addToast: () => {},
    removeToast: () => {},
    refreshShortcodes: () => {},
    saveNewShortcode: (formData) => {},
    showEditModal: (shortcodeId) => {},
    updateShortcode: (id, formData) => {},
    handleSubmit: () => {},
    closeShortCodeModal: () => {},
    handleInputChange: () => {},
    shortcodeFormData: null,
    setShortcodeFormData: null,
    shortcodeModalActiveTab: null,
    setShortcodeModalActiveTab: null,
    bulkAction: null,
    setBulkAction: null,
    handleBulkActions: () => {},
});

export function RulesContextProvider({ children }) {
    const { addToast, removeToast, clearAllToasts, toasts } = useToast();
    const [shortcodes, setShortcodes] = useState([]);
    const [editShortcodeData, setEditShortcodeData] = useState(null);
    const [productTypes, setProductTypes] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [showAddModal, setShowAddModal] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState({
        shortcodes: false, // fetch all shortcodes loading
        singleShortcode: false, // fetch single shortcode loading
        formSubmit: false, // form submit loading
    });
    const [shortcodeModalActiveTab, setShortcodeModalActiveTab] =
        useState("basic");
    const [bulkAction, setBulkAction] = useState("");
    // For Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    // Calculate pagination indices
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    // For sortting
    const [sortField, setSortField] = useState("id");
    const [sortDirection, setSortDirection] = useState("DESC");

    const initialShortcodeFormData = {
        id: "",
        name: "",
        rule_priority: 10,
        apply_to: "products",
        min_quantity: 1,
        max_quantity: 100,
        step: 1,
        default_quantity: 1,
        product_types: [],
        user_roles: [],
        products: [],
        categories: [],
        tags: [],
    };
    const [shortcodeFormData, setShortcodeFormData] = useState(
        initialShortcodeFormData,
    );
    const [qtyValidationMessages, setQtyValidationMessages] = useState([]);

    // Update form data when shortcode prop changes (when its a shortcode edit popup form)
    useEffect(() => {
        if (editShortcodeData) {
            setShortcodeFormData({
                id: editShortcodeData.id || "",
                name: editShortcodeData.name || "",
                rule_priority: editShortcodeData.rule_priority || 1,
                apply_to: editShortcodeData.apply_to || "products",
                min_quantity: editShortcodeData.min_quantity || 1,
                max_quantity: editShortcodeData.max_quantity || 100,
                step: editShortcodeData.step || 1,
                default_quantity: editShortcodeData.default_quantity || 1,
                user_roles: editShortcodeData.user_roles || [],
                product_types: editShortcodeData.product_types || [],
                products: editShortcodeData.products || [],
                categories: editShortcodeData.categories || [],
                tags: editShortcodeData.tags || [],
            });
        } else {
            // Reset to default values for new shortcode
            setShortcodeFormData(initialShortcodeFormData);
        }
    }, [editShortcodeData]);

    /**
     * Fetch shortcodes from REST API
     */
    const fetchShortcodes = useCallback(async () => {
        setLoading((prev) => ({ ...prev, shortcodes: true }));
        setError(null);
        try {
            const response = await apiFetch({
                path: "/wpb-pmqb-quantity/v1/rules",
            });
            setShortcodes(response || []);
        } catch (err) {
            console.error("Fetch rules error:", err);
            setError(err.message || "Failed to fetch rules");
            addToast({
                type: "error",
                message: `Failed to fetch rules: ${
                    err.message || "Unknown error"
                }`,
            });
        } finally {
            setLoading((prev) => ({ ...prev, shortcodes: false }));
        }
    }, []);

    // Load shortcodes on mount
    useEffect(() => {
        fetchShortcodes();
    }, [fetchShortcodes]);

    // Fetch WC Product Types
    const fetchProductTypes = useCallback(async () => {
        try {
            const response = await apiFetch({
                path: "/wpb-pmqb-quantity/v1/product-types",
            });
            setProductTypes(
                Object.entries(response).map(([value, label]) => ({
                    value,
                    label,
                })),
            );
        } catch (err) {
            console.error("Failed to fetch product types:", err);
        }
    }, []);

    useEffect(() => {
        fetchProductTypes();
    }, [fetchProductTypes]);

    // Fetch WP User Roles
    const fetchUserRoles = useCallback(async () => {
        try {
            const response = await apiFetch({
                path: "/wpb-pmqb-quantity/v1/user-roles",
            });
            setUserRoles(
                Object.entries(response).map(([value, label]) => ({
                    value,
                    label,
                })),
            );
        } catch (err) {
            console.error("Failed to fetch user roles:", err);
        }
    }, []);

    useEffect(() => {
        fetchUserRoles();
    }, [fetchUserRoles]);

    // Save new shortcode
    const saveNewShortcode = async (formData) => {
        //console.log("Saving shortcode:", formData);

        // Validate required fields on frontend
        if (!formData.name) {
            addToast({
                type: "error",
                message: "Name is a required field",
            });
            return false;
        }

        try {
            setLoading((prev) => ({ ...prev, shortcodes: true }));
            setError(null);

            const response = await apiFetch({
                path: "/wpb-pmqb-quantity/v1/rules",
                method: "POST",
                data: formData, // Use 'data' instead of 'body' for WordPress API
            });

            //console.log("Save response:", response);

            // Add the new shortcode to state
            if (response && response.id) {
                setShortcodes((prev) => [response, ...prev]);

                addToast({
                    type: "success",
                    message: "Rule saved successfully!",
                });

                return true;
            } else {
                throw new Error("Invalid response from server");
            }
        } catch (err) {
            console.error("Save rule error:", err);
            const errorMessage = err.message || "Failed to save rule";

            setError(errorMessage);
            addToast({
                type: "error",
                message: `Failed to save rule: ${errorMessage}`,
            });

            return false;
        } finally {
            setLoading((prev) => ({ ...prev, shortcodes: false }));
        }
    };

    // Get single shortcode
    const getSingleShortcode = async (id) => {
        try {
            //setSingleShortcodeLoading(true);
            setLoading((prev) => ({ ...prev, singleShortcode: true }));
            setError(null);

            const response = await apiFetch({
                path: `/wpb-pmqb-quantity/v1/rule/${id}`,
            });

            if (response) {
                return response;
            } else {
                throw new Error("Rule not found");
            }
        } catch (err) {
            console.error("Get rule error:", err);
            const errorMessage = err.message || "Failed to get rule";

            setError(errorMessage);
            addToast({
                type: "error",
                message: `Failed to get rule: ${errorMessage}`,
            });

            return null;
        } finally {
            setLoading((prev) => ({ ...prev, singleShortcode: false }));
        }
    };

    // Show edit shortcode modal
    const showEditModal = useCallback(
        async (shortcodeId) => {
            setShowAddModal(true);
            const data = await getSingleShortcode(shortcodeId);
            setEditShortcodeData(data);
        },
        [getSingleShortcode],
    );

    // Update existing shortcode
    const updateShortcode = async (id, formData) => {
        //console.log("Updating shortcode:", id, formData);
        if (!formData.name) {
            addToast({
                type: "error",
                message: "Name is a required field",
            });
            return false;
        }

        try {
            setLoading((prev) => ({ ...prev, shortcodes: true }));
            setError(null);

            const response = await apiFetch({
                path: `/wpb-pmqb-quantity/v1/rule/${id}`,
                method: "PUT",
                data: formData,
            });

            //console.log("Update response:", response);

            if (response && response.id) {
                // Update the shortcode in state
                setShortcodes((prev) =>
                    prev.map((shortcode) =>
                        shortcode.id === id ? response : shortcode,
                    ),
                );

                addToast({
                    type: "success",
                    message: "Rule updated successfully!",
                });

                return true;
            } else {
                throw new Error("Invalid response from server");
            }
        } catch (err) {
            console.error("Update rule error:", err);
            const errorMessage = err.message || "Failed to update rule";

            setError(errorMessage);
            addToast({
                type: "error",
                message: `Failed to update rule: ${errorMessage}`,
            });

            return false;
        } finally {
            setLoading((prev) => ({ ...prev, shortcodes: false }));
        }
    };

    /**
     * Move to trash a shortcode by ID
     */
    const trashShortcode = useCallback(async (id) => {
        try {
            setLoading((prev) => ({ ...prev, shortcodes: true }));
            setError(null);

            const response = await apiFetch({
                path: `/wpb-pmqb-quantity/v1/rules/${id}/trash`,
                method: "POST",
            });

            // Optimistically update local state
            setShortcodes((prev) =>
                prev.map((shortcode) =>
                    shortcode.id === id
                        ? { ...shortcode, rule_status: "trash" }
                        : shortcode,
                ),
            );

            addToast({
                type: "success",
                message: response.message || "Rule moved to trash.",
            });

            return true;
        } catch (err) {
            console.error("Trash rule error:", err);
            const errorMessage = err.message || "Failed to move rule to trash.";

            setError(errorMessage);
            addToast({
                type: "error",
                message: `Error: ${errorMessage}`,
            });

            return false;
        } finally {
            setLoading((prev) => ({ ...prev, shortcodes: false }));
        }
    }, []);

    /**
     * Duplicate a shortcode by ID
     */
    const duplicateShortcode = useCallback(async (id) => {
        try {
            setLoading((prev) => ({ ...prev, shortcodes: true }));
            setError(null);

            const response = await apiFetch({
                path: `/wpb-pmqb-quantity/v1/rules/${id}/duplicate`,
                method: "POST",
            });

            if (response.success) {
                addToast({
                    type: "success",
                    message:
                        response.message || "Rule duplicated successfully!",
                });

                // Refetch updated shortcode list
                fetchShortcodes?.();
            } else {
                throw new Error(response.message || "Failed to duplicate rule");
            }
        } catch (err) {
            console.error("Duplicate rule error:", err);
            const errorMessage = err.message || "Failed to duplicate rule";

            setError(errorMessage);
            addToast({
                type: "error",
                message: `Failed to duplicate rule: ${errorMessage}`,
            });
        } finally {
            setLoading((prev) => ({ ...prev, shortcodes: false }));
        }
    }, []);

    /**
     * Delete a shortcode by ID
     */
    const deleteShortcode = useCallback(async (id) => {
        try {
            setLoading((prev) => ({ ...prev, shortcodes: true }));
            setError(null);

            await apiFetch({
                path: `/wpb-pmqb-quantity/v1/rule/${id}`,
                method: "DELETE",
            });

            // Optimistically update state
            setShortcodes((prev) =>
                prev.filter((shortcode) => shortcode.id !== id),
            );

            addToast({
                type: "success",
                message: "Rule deleted successfully!",
            });

            return true;
        } catch (err) {
            console.error("Delete rule error:", err);
            const errorMessage = err.message || "Failed to delete rule";

            setError(errorMessage);
            addToast({
                type: "error",
                message: `Failed to delete rule: ${errorMessage}`,
            });

            return false;
        } finally {
            setLoading((prev) => ({ ...prev, shortcodes: false }));
        }
    }, []);

    // Derived state: filtered and sorted shortcodes
    const filteredShortcodes = shortcodes
        .filter((item) => {
            const term = (searchTerm || "").toLowerCase();

            const matchesSearch =
                item.name && item.name.toString().toLowerCase().includes(term);

            const matchesStatus =
                filterStatus === "all" ||
                (item.rule_status &&
                    item.rule_status.toLowerCase() === filterStatus);

            return matchesSearch && matchesStatus;
        })
        .sort((a, b) => {
            // Id sort
            if (sortField === "id") {
                const idA = a.id;
                const idB = b.id;
                return sortDirection === "ASC" ? idA - idB : idB - idA;
            }

            // Date sort
            if (sortField === "date") {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return sortDirection === "ASC" ? dateA - dateB : dateB - dateA;
            }

            // name sort
            if (sortField === "name") {
                return sortDirection === "ASC"
                    ? a.name.localeCompare(b.name)
                    : b.name.localeCompare(a.name);
            }

            // Priority sort
            if (sortField === "priority") {
                const priorityA = Number(a.rule_priority);
                const priorityB = Number(b.rule_priority);
                return sortDirection === "ASC"
                    ? priorityA - priorityB
                    : priorityB - priorityA;
            }

            // Min sort
            if (sortField === "min") {
                const minA = Number(a.min_quantity);
                const minB = Number(b.min_quantity);
                return sortDirection === "ASC" ? minA - minB : minB - minA;
            }

            // Max sort
            if (sortField === "max") {
                const maxA = Number(a.max_quantity);
                const maxB = Number(b.max_quantity);
                return sortDirection === "ASC" ? maxA - maxB : maxB - maxA;
            }

            // Step sort
            if (sortField === "step") {
                const stepA = Number(a.step);
                const stepB = Number(b.step);
                return sortDirection === "ASC" ? stepA - stepB : stepB - stepA;
            }

            // Default sort
            if (sortField === "default") {
                const defaultA = Number(a.default_quantity);
                const defaultB = Number(b.default_quantity);
                return sortDirection === "ASC"
                    ? defaultA - defaultB
                    : defaultB - defaultA;
            }

            // rule_status sort
            if (sortField === "rule_status") {
                return sortDirection === "ASC"
                    ? a.rule_status.localeCompare(b.rule_status)
                    : b.rule_status.localeCompare(a.rule_status);
            }

            return 0;
        });

    // Slice the filtered + sorted list for pagination
    const currentShortcodes = filteredShortcodes.slice(
        indexOfFirstItem,
        indexOfLastItem,
    );

    // Calculate total pages
    const totalPages = Math.ceil(filteredShortcodes.length / itemsPerPage);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterStatus, sortField, sortDirection]);

    // Select item
    const handleSelectItem = (id, checked) => {
        const newSelected = new Set(selectedItems);
        if (checked) newSelected.add(id);
        else newSelected.delete(id);
        setSelectedItems(newSelected);
    };

    // Select all
    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedItems(new Set(currentShortcodes.map((item) => item.id)));
        } else {
            setSelectedItems(new Set());
        }
    };

    // Close ShortCode add/edit modal
    const closeShortCodeModal = () => {
        setShowAddModal(false);
        setEditShortcodeData(null);
        setShortcodeModalActiveTab("basic");
    };

    // ShortCode form input change callback
    const handleInputChange = (field, value) => {
        setShortcodeFormData((prev) => {
            const updatedForm = { ...prev, [field]: value };
            // Validate quantity fields and update messages
            const min = Number(updatedForm.min_quantity) || 0;
            const max = Number(updatedForm.max_quantity) || 0;
            const step = Number(updatedForm.step) || 0;
            const def = Number(updatedForm.default_quantity) || 0;

            const messages = [];

            // 1) max must be greater than min
            if (max <= min) {
                messages.push(
                    __(
                        "Maximum quantity must be greater than minimum quantity.",
                        "wpbean-quantity-controls-for-woocommerce",
                    ),
                );
            }

            // 2) step > 0 and (if max > 0) step < max
            if (step <= 0) {
                messages.push(
                    __(
                        "Step must be greater than zero.",
                        "wpbean-quantity-controls-for-woocommerce",
                    ),
                );
            } else if (max > 0 && step >= max) {
                messages.push(
                    __(
                        "Step value should be less than the maximum quantity.",
                        "wpbean-quantity-controls-for-woocommerce",
                    ),
                );
            }

            // 3) default between min and max
            if (def < min) {
                messages.push(
                    __(
                        "Default quantity must be greater than or equal to the minimum quantity.",
                        "wpbean-quantity-controls-for-woocommerce",
                    ),
                );
            }
            if (max > 0 && def > max) {
                messages.push(
                    __(
                        "Default quantity must be less than or equal to the maximum quantity.",
                        "wpbean-quantity-controls-for-woocommerce",
                    ),
                );
            }

            // 4) default aligns with step interval from min
            if (step > 0 && def >= min) {
                const offset = def - min;
                if (offset % step !== 0) {
                    messages.push(
                        __(
                            "Default quantity does not match the step interval from minimum quantity.",
                            "wpbean-quantity-controls-for-woocommerce",
                        ),
                    );
                }
            }

            // Update validation messages state
            setQtyValidationMessages(messages);

            return updatedForm;
        });
    };

    // Handle ShortCode add/edit form submit
    const handleSubmit = async () => {
        setLoading((prev) => ({ ...prev, formSubmit: true }));

        try {
            let success = false;

            if (editShortcodeData && editShortcodeData.id) {
                // Edit mode
                success = await updateShortcode(
                    editShortcodeData.id,
                    shortcodeFormData,
                );
            } else {
                // Add mode
                success = await saveNewShortcode(shortcodeFormData);
            }

            if (success) {
                setShowAddModal(false);
                setEditShortcodeData(null);
                setShortcodeFormData(initialShortcodeFormData);
                setShortcodeModalActiveTab("basic");
            }
        } catch (error) {
            console.error("Error saving/updating rule:", error);
            setError(error.message || "Failed to saving/updating rule");
            addToast({
                type: "error",
                message: `Failed to saving rule: ${
                    error.message || "Unknown error"
                }`,
            });
        } finally {
            setLoading((prev) => ({ ...prev, formSubmit: false }));
        }
    };

    const handleBulkActions = async (actionParam) => {
        const actionToUse = actionParam || bulkAction;

        if (!actionToUse || actionToUse === "none")
            return alert("Select a bulk action.");

        if (selectedItems.size === 0) return alert("Select at least one item.");

        const ids = Array.from(selectedItems);

        try {
            const response = await wp.apiFetch({
                path: "/wpb-pmqb-quantity/v1/bulk-update",
                method: "POST",
                data: { action: actionToUse, ids },
            });

            if (response.success) {
                addToast({
                    type: "success",
                    message: response.message,
                });
                fetchShortcodes?.();

                // 🧭 Optional: Adjust pagination after trash/empty_trash
                if (
                    actionToUse === "empty_trash" &&
                    typeof setCurrentPage === "function"
                ) {
                    setCurrentPage((prev) => {
                        const remaining = shortcodes.length - ids.length;
                        const newTotalPages = Math.max(
                            1,
                            Math.ceil(remaining / itemsPerPage),
                        );
                        return prev > newTotalPages ? newTotalPages : prev;
                    });
                }
            } else {
                addToast({
                    type: "error",
                    message: response.message || "Something went wrong!",
                });
            }
        } catch (error) {
            setError(error.message || "Bulk action error.");
            addToast({
                type: "error",
                message: `Bulk action error: ${
                    error.message || "Error applying bulk action."
                }`,
            });
        } finally {
            setSelectedItems(new Set());
            setBulkAction(null);
        }
    };

    const ctxValue = {
        shortcodes,
        filteredShortcodes,
        productTypes,
        userRoles,
        searchTerm,
        setSearchTerm,
        selectedItems,
        setSelectedItems,
        showAddModal,
        setShowAddModal,
        editShortcodeData,
        setEditShortcodeData,
        filterStatus,
        setFilterStatus,
        handleSelectItem,
        handleSelectAll,
        loading,
        setLoading,
        error,
        addToast,
        removeToast,
        clearAllToasts,
        toasts,
        refreshShortcodes: fetchShortcodes,
        deleteShortcode,
        trashShortcode,
        duplicateShortcode,
        saveNewShortcode,
        showEditModal,
        updateShortcode,
        handleSubmit,
        closeShortCodeModal,
        handleInputChange,
        shortcodeFormData,
        setShortcodeFormData,
        shortcodeModalActiveTab,
        setShortcodeModalActiveTab,
        bulkAction,
        setBulkAction,
        handleBulkActions,
        itemsPerPage,
        currentPage,
        setCurrentPage,
        currentShortcodes,
        totalPages,
        sortField,
        setSortField,
        sortDirection,
        setSortDirection,
        qtyValidationMessages,
    };

    return (
        <RulesContext.Provider value={ctxValue}>
            {children}
        </RulesContext.Provider>
    );
}
